/**
 * PortfoliYo Lab — how big a definition is, and what that means for showing it.
 *
 * One place, because two pages learned the same lesson from the same file. `<textarea>` does not
 * virtualize: handed `VRK_Borg_VIX_RSI_Cascade.json` — 30,444,403 characters on a SINGLE line, no
 * newline anywhere — Chrome lays out one thirty-million-character wrapped line and the tab stops
 * responding. The Visualizer hit it first; the New Strategy page had exactly the same textarea and
 * exactly the same hang.
 *
 * The thresholds are measured against the real strategies rather than picked: the VIX satellite
 * sleeve is 18 KB and edits comfortably, Borg Core is 826 KB and the cascade is 30 MB, both
 * minified onto one line, and neither is something a person edits in a browser.
 */

/** Above this a definition is shown, never typed into. */
export const EDITABLE_MAX_CHARS = 200_000;

/** How much of an oversized definition to show. Enough to identify it, cheap enough to lay out. */
export const PREVIEW_CHARS = 20_000;

/** A glance at what is loaded, beside a form. Deliberately far smaller than a preview. */
export const EXCERPT_CHARS = 400;

/**
 * Is a definition of this many characters too big to put in a textarea at all?
 *
 * By count rather than by text, because since #418 the editor knows how long a stored definition
 * is before — and usually instead of — holding it: the server reports the length, and the text is
 * fetched only if this says it may be edited.
 */
export function isTooManyCharsToEdit(chars: number): boolean {
  return chars > EDITABLE_MAX_CHARS;
}

/** Is this too big to put in a textarea at all? */
export function isTooLargeToEdit(text: string): boolean {
  return isTooManyCharsToEdit(text.length);
}

/**
 * A human-sized description of the definition's length.
 *
 * Characters below a kilobyte, because a 400-character strategy reads as 400 characters and not as
 * "0.0 MB" — the units follow the magnitude rather than being fixed, so the number always carries
 * information.
 */
export function describeSize(text: string): string {
  return describeChars(text.length);
}

/** `describeSize`, for a length the server reported without the text (#418). */
export function describeChars(n: number): string {
  if (n < 1_000) return `${n} characters`;
  if (n < 1_000_000) return `${(n / 1_000).toFixed(1)} KB`;
  return `${(n / 1_000_000).toFixed(1)} MB`;
}
